import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import GameService from '../../../../../services/game.service';
import { getComparator, getFormattedDate, stableSort } from '../../../components/utilities';
import '../../../coach_style.css';
import TeamGamesVideoPlayer from './videoDialog';
import { getPeriod } from '../../../games/tabs/overview/tagListItem';
import { ActionData, isGoalTag } from '../../../components/common';
import GameExportToEdits from '../../../games/tabs/overview/exportEdits';
import GeniusBallLogo from '../../../../../assets/images/football/Genius-Ball-Logo_Original Colour - Black.png';
import s4uLogo from '../../../../../assets/images/football/SmallerS4ULogo.png';
import './DataAnalysis.css';

// Stats grouped by category for better organization
const offensiveProps = [
    { id: 'total_goal', action: 'Goal', label: 'Goals' },
    { id: 'total_shot', action: 'GoalKick', label: 'Shots' },
    { id: 'total_shot_on_target', action: 'ShotOnTarget', label: 'On Target', pctOf: 'total_shot' },
    { id: 'total_passes', action: 'Passes', label: 'Passes' },
    { id: 'total_successful_passes', action: 'PassesSuccess', label: 'Completed', pctOf: 'total_passes' },
    { id: 'total_crosses', action: 'Cross', label: 'Crosses' },
    { id: 'total_cross_successful', action: 'CrossSuccess', label: 'Completed', pctOf: 'total_crosses' },
    { id: 'total_dribble', action: 'Dribble', label: 'Dribbles' }
];

const defensiveProps = [
    { id: 'total_interception', action: 'Interception', label: 'Interceptions' },
    { id: 'total_tackle', action: 'Tackle', label: 'Tackles' },
    { id: 'total_turnover', action: 'Turnover', label: 'Turnovers' }
];

const setPieceProps = [
    { id: 'total_corners', action: 'Corner', label: 'Corners' },
    { id: 'total_free_kick', action: 'FreeKick', label: 'Free Kicks' },
    { id: 'total_fouls', action: 'Foul', label: 'Fouls' },
    { id: 'total_throw_in', action: 'ThrowIn', label: 'Throw-Ins' },
    { id: 'total_penalty', action: 'PenaltyWon', label: 'Penalties Won' }
];

// Combined properties for backward compatibility
const properties = [...offensiveProps, ...defensiveProps, ...setPieceProps];

// Helper to calculate percentage
const calcPct = (value, total) => {
    if (!total || total === 0) return null;
    return Math.round((value / total) * 100);
};

// Helper to get stat value based on team position
const getStatValue = (item, propId, isOurTeam) => {
    const isHomeTeam = item.team_id === item.home_team_id;
    if (isOurTeam) {
        return isHomeTeam ? item[propId] : item[`opp_${propId}`];
    }
    return isHomeTeam ? item[`opp_${propId}`] : item[propId];
};

const TeamGames = ({ games, gameIds, teamId, seasonId, t, teamName, teamShortName, teamLogo, }) => {
    const [teamStatList, setTeamStatList] = useState([]);
    const [teamSummaryStatList, setTeamSummaryStatList] = useState([]);
    const [playData, setPlayData] = useState([]);
    const [videoOpen, setVideoOpen] = useState(false);
    // Which column was clicked, so the clip list can say what is being watched.
    const [videoTitle, setVideoTitle] = useState('');
    const [videoURL, setVideoURL] = useState('');
    const [exportOpen, setExportOpen] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [hoverIndex, setHoverIndex] = useState(-1);
    const [loading, setLoading] = useState(false);
    const { user: currentUser } = useSelector((state) => state.auth);
    const tableDataAnalysis = useRef(null);

    const handleDisplayVideo = (cell, isOur, prop) => {
        const value = cell.team_id === cell.home_team_id ? (isOur ? cell[prop.id] : cell[`opp_${prop.id}`]) : isOur ? cell[`opp_${prop.id}`] : cell[prop.id];

        if (value <= 0) return;

        setVideoTitle(prop.label || '');

        const actionData = ActionData[prop.action];

        GameService.getGamePlayerTags(
            currentUser.id,
            isOur ? cell.home_team_id : cell.away_team_id,
            null,
            `${cell.game_id}`,
            actionData.action_id,
            actionData.action_type_id,
            actionData.action_result_id,
            null,
            null,
            null,
            null,
            null
        ).then((res) => {
            // Filter out excluded action types (e.g., corners from crosses)
            let filteredRes = res;
            if (actionData.excludeActionTypeId) {
                filteredRes = res.filter((item) => item.action_type_id !== actionData.excludeActionTypeId);
            }
            // Goal clips are shots only; assists are also tagged result=Goal, so filter them out.
            if (prop.action === 'Goal') {
                filteredRes = filteredRes.filter(isGoalTag);
            }

            setPlayData(
                filteredRes.map((item) => {
                    return {
                        tag_id: item.id,
                        start_time: item.player_tag_start_time,
                        end_time: item.player_tag_end_time,
                        player_name: item.player_names || item.player_name || item.full_name || '',
                        action_name: item.action_names || item.action_name || '',
                        action_type: item.action_type_names || item.action_type_name || item.action_type || '',
                        action_result: item.action_result_names || item.action_result_name || item.action_result || '',
                        game_id: item.game_id,
                        team_id: teamId,
                        court_area: item.court_area_id,
                        inside_pain: item.inside_the_paint,
                        period: item.period,
                        time_in_game: item.time_in_game,
                        home_team_logo: item.home_team_logo,
                        away_team_logo: item.away_team_logo,
                        home_team_goals: item.home_team_goals ?? item.home_team_goal,
                        away_team_goals: item.away_team_goals ?? item.away_team_goal
                    };
                })
            );
            setVideoURL(games.filter((item) => item.id === cell.game_id)[0].video_url);
            setVideoOpen(true);
        });
    };

    const handleExportTags = (cell, isOur, prop) => (e) => {
        e.preventDefault();

        const value = cell.team_id === cell.home_team_id ? (isOur ? cell[prop.id] : cell[`opp_${prop.id}`]) : isOur ? cell[`opp_${prop.id}`] : cell[prop.id];

        if (value <= 0) return;

        const actionData = ActionData[prop.action];

        GameService.getGamePlayerTags(
            currentUser.id,
            isOur ? cell.home_team_id : cell.away_team_id,
            null,
            `${cell.game_id}`,
            actionData.action_id,
            actionData.action_type_id,
            actionData.action_result_id,
            null,
            null,
            null,
            null,
            null
        ).then((res) => {
            // Filter out excluded action types (e.g., corners from crosses)
            let filteredRes = res;
            if (actionData.excludeActionTypeId) {
                filteredRes = res.filter((item) => item.action_type_id !== actionData.excludeActionTypeId);
            }
            // Goal clips are shots only; assists are also tagged result=Goal, so filter them out.
            if (prop.action === 'Goal') {
                filteredRes = filteredRes.filter(isGoalTag);
            }

            setPlayData(filteredRes);
            setExportOpen(true);
        });
    };

    const getGameResultImage = (game) => {
        const isOur = game.team_id === game.home_team_id ? 1 : game.team_id === game.away_team_id ? 2 : 0;

        if (isOur !== 0) {
            if (game['total_goal'] > game['opp_total_goal']) {
                if (isOur === 1) return { home: { text: 'W', color: '#52B030' }, away: { text: '', color: '#ffffff' } };
                else return { home: { text: '', color: '#ffffff' }, away: { text: 'W', color: '#52B030' } };
            } else if (game['total_goal'] < game['opp_total_goal']) {
                if (isOur === 1) return { home: { text: 'L', color: '#C1272D' }, away: { text: '', color: '#ffffff' } };
                else return { home: { text: '', color: '#ffffff' }, away: { text: 'L', color: '#C1272D' } };
            } else {
                if (isOur === 1) return { home: { text: 'D', color: '#A4AAB0' }, away: { text: '', color: '#ffffff' } };
                else return { home: { text: '', color: '#ffffff' }, away: { text: 'D', color: '#A4AAB0' } };
            }
        }

        return { home: { text: '', color: '#ffffff' }, away: { text: '', color: '#ffffff' } };
    };

    const getGameGoalsFontStyle = (game) => {
        if (game.team_id === game.home_team_id) return { home: 700, away: 400 };

        return { home: 400, away: 700 };
    };

    useEffect(() => {
        if (gameIds.length > 0) setLoading(false);
    }, [teamSummaryStatList]);

    useEffect(() => {
        if (gameIds.length > 0) {
            setLoading(true);
            GameService.getTeamsStatsGamebyGameSummary({
                seasonId: seasonId,
                leagueId: null,
                gameIds: gameIds.join(','),
                teamId: teamId,
                gameTime: null,
                courtAreaId: null,
                insidePaint: null,
                homeAway: null,
                gameResult: null
            }).then((res) => {
                setTeamSummaryStatList(stableSort(res, getComparator('desc', 'game_date')));
            });

            GameService.getTeamsStatsGamebyGame({
                seasonId: seasonId,
                leagueId: null,
                gameId: gameIds.join(','),
                teamId: teamId,
                gameTime: null,
                courtAreaId: null,
                insidePaint: null,
                homeAway: null,
                gameResult: null
            }).then((res) => {
                setTeamStatList(stableSort(res, getComparator('desc', 'game_date')));
            });
        } else setTeamSummaryStatList([]);
    }, [gameIds, refresh]);

    return (
        <Box sx={{ width: '100%', background: '#ffffff', height: '75vh', display: 'flex', padding: '20px 10px' }}>
            {loading && (
                <div style={{ width: '100%', height: '100%', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    <Box className="printable-content" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <img className="headerGeniusballLogo" src={GeniusBallLogo} />
                        <img className="headerTeamLogo" src={teamLogo} />
                        <p className="headerTeamName">{teamName}</p>
                        <p className="headerReportName">Data Analysis Game By Game</p>
                        <img className="headerS4ULogo" src={s4uLogo} />
                        <TableContainer className="tableDataAnalysis" ref={tableDataAnalysis} >
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    {/* Group Headers Row */}
                                    <TableRow height="28px">
                                        <TableCell rowSpan={2} style={{ width: '100px', background: '#f5f5f5' }} align="center">
                                            {t('Game')}
                                        </TableCell>
                                        <TableCell rowSpan={2} colSpan={2} style={{ background: '#f5f5f5' }} align="center">
                                            {t('Team')}
                                        </TableCell>
                                        <TableCell rowSpan={2} style={{ background: '#f5f5f5' }} align="center">
                                            {t('Formation')}
                                        </TableCell>
                                        <TableCell
                                            colSpan={offensiveProps.length}
                                            align="center"
                                            className="header-group-offensive"
                                            sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}
                                        >
                                            {t('Offensive')}
                                        </TableCell>
                                        <TableCell
                                            colSpan={defensiveProps.length}
                                            align="center"
                                            className="header-group-defensive"
                                            sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}
                                        >
                                            {t('Defensive')}
                                        </TableCell>
                                        <TableCell
                                            colSpan={setPieceProps.length}
                                            align="center"
                                            className="header-group-setpieces"
                                            sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}
                                        >
                                            {t('Set Pieces')}
                                        </TableCell>
                                    </TableRow>
                                    {/* Column Headers Row */}
                                    <TableRow height="32px">
                                        {offensiveProps.map((prop) => (
                                            <TableCell
                                                key={prop.id}
                                                align="center"
                                                className="stat-header"
                                                sx={{ background: '#E8EEF4', borderBottom: '2px solid #17375E' }}
                                            >
                                                {t(prop.label)}
                                            </TableCell>
                                        ))}
                                        {defensiveProps.map((prop) => (
                                            <TableCell
                                                key={prop.id}
                                                align="center"
                                                className="stat-header"
                                                sx={{ background: '#E8F4E8', borderBottom: '2px solid #0A7304' }}
                                            >
                                                {t(prop.label)}
                                            </TableCell>
                                        ))}
                                        {setPieceProps.map((prop) => (
                                            <TableCell
                                                key={prop.id}
                                                align="center"
                                                className="stat-header"
                                                sx={{ background: '#F4E8E8', borderBottom: '2px solid #C5102C' }}
                                            >
                                                {t(prop.label)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {teamStatList.map((item, index) => {
                                        const isHomeOurTeam = item.team_id === item.home_team_id;
                                        return (
                                            <React.Fragment key={`teamStat-${index}`}>
                                                {/* Our Team Row */}
                                                <TableRow
                                                    key={`${index}-our`}
                                                    height="36px"
                                                    className="our-team-row"
                                                    onMouseEnter={() => setHoverIndex(index)}
                                                    onMouseLeave={() => setHoverIndex(-1)}
                                                >
                                                    <TableCell key={`${index}-game`} align="center" rowSpan={2} sx={{ borderBottom: '2px solid #17375E', background: '#f9f9f9' }}>
                                                        <div>
                                                            <p className="normal-text" style={{ fontWeight: 600, color: '#17375E' }}>{item.league_name}</p>
                                                            <p className="normal-text" style={{ fontSize: '0.75rem' }}>
                                                                {getFormattedDate(item.game_date)}
                                                            </p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell style={{ width: '30px' }} key={`${index}-result`} align="center">
                                                        <div
                                                            style={{
                                                                background: isHomeOurTeam ? getGameResultImage(item)['home'].color : getGameResultImage(item)['away'].color,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '24px',
                                                                height: '24px',
                                                                borderRadius: '4px'
                                                            }}
                                                        >
                                                            <p className="normal-text-white">{isHomeOurTeam ? getGameResultImage(item)['home'].text : getGameResultImage(item)['away'].text}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell key={`${index}-our-name`} align="center" sx={{ fontWeight: 700, color: '#17375E' }}>
                                                        {isHomeOurTeam ? item.home_team_name : item.away_team_name}
                                                    </TableCell>
                                                    <TableCell key={`${index}-our-format`} align="center" sx={{ fontWeight: 600 }}>
                                                        {item.team_formation_name?.toLowerCase().includes('unknown') ? '' : item.team_formation_name}
                                                    </TableCell>
                                                    {properties.map((prop) => {
                                                        const value = getStatValue(item, prop.id, true);
                                                        const pct = prop.pctOf ? calcPct(value, getStatValue(item, prop.pctOf, true)) : null;
                                                        return (
                                                            <TableCell
                                                                key={`${index}-our-${prop.id}`}
                                                                align="center"
                                                                className="stat-cell-clickable"
                                                                onClick={() => handleDisplayVideo(item, true, prop)}
                                                                onContextMenu={handleExportTags(item, true, prop)}
                                                            >
                                                                <span style={{ fontWeight: 600 }}>{value}</span>
                                                                {pct !== null && <span className="percentage-cell"> ({pct}%)</span>}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                                {/* Opponent Row */}
                                                <TableRow key={`${index}-opp`} height="36px" className="opponent-row">
                                                    <TableCell key={`${index}-opp-result`} align="center" sx={{ borderBottom: '2px solid #17375E' }}>
                                                        <div
                                                            style={{
                                                                background: isHomeOurTeam ? getGameResultImage(item)['away'].color : getGameResultImage(item)['home'].color,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '24px',
                                                                height: '24px',
                                                                borderRadius: '4px',
                                                                opacity: 0.7
                                                            }}
                                                        >
                                                            <p className="normal-text-white">{isHomeOurTeam ? getGameResultImage(item)['away'].text : getGameResultImage(item)['home'].text}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell key={`${index}-opp-name`} align="center" sx={{ borderBottom: '2px solid #17375E', color: '#666' }}>
                                                        {isHomeOurTeam ? item.away_team_name : item.home_team_name}
                                                    </TableCell>
                                                    <TableCell key={`${index}-opp-format`} align="center" sx={{ borderBottom: '2px solid #17375E', color: '#888' }}>
                                                        {item.opponent_formation_name?.toLowerCase().includes('unknown') ? '' : item.opponent_formation_name}
                                                    </TableCell>
                                                    {properties.map((prop) => {
                                                        const value = getStatValue(item, prop.id, false);
                                                        const pct = prop.pctOf ? calcPct(value, getStatValue(item, prop.pctOf, false)) : null;
                                                        return (
                                                            <TableCell
                                                                key={`${index}-opp-${prop.id}`}
                                                                align="center"
                                                                className="stat-cell-clickable"
                                                                sx={{ borderBottom: '2px solid #17375E' }}
                                                                onClick={() => handleDisplayVideo(item, false, prop)}
                                                                onContextMenu={handleExportTags(item, false, prop)}
                                                            >
                                                                <span>{value}</span>
                                                                {pct !== null && <span className="percentage-cell"> ({pct}%)</span>}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}

                                    {teamSummaryStatList.map((item, index) => (
                                        <React.Fragment key={`teamSummaryStat-${index}`}>
                                            <TableRow key={`${index}-summary`} height="44px" className="summary-row">
                                                <TableCell
                                                    key={`${index}-game-summary`}
                                                    align="center"
                                                    sx={{ background: '#17375E', color: 'white', fontWeight: 800, fontSize: '0.85rem' }}
                                                >
                                                    {t('TOTALS')}
                                                </TableCell>
                                                <TableCell sx={{ background: '#f0f4f8' }}></TableCell>
                                                <TableCell sx={{ background: '#f0f4f8', fontWeight: 700, color: '#17375E' }} align="center">
                                                    {item.total_game} {t('Games')}
                                                </TableCell>
                                                <TableCell sx={{ background: '#f0f4f8' }}></TableCell>
                                                {properties.map((prop) => {
                                                    const value = item[prop.id] || 0;
                                                    const pctOfValue = prop.pctOf ? (item[prop.pctOf] || 0) : null;
                                                    const pct = pctOfValue ? calcPct(value, pctOfValue) : null;
                                                    return (
                                                        <TableCell
                                                            key={`${index}-${prop.id}-summary`}
                                                            align="center"
                                                            sx={{
                                                                cursor: 'context-menu',
                                                                background: '#f0f4f8',
                                                                fontWeight: 700,
                                                                color: '#17375E'
                                                            }}
                                                            onContextMenu={handleExportTags(item, true, prop)}
                                                        >
                                                            <span style={{ fontSize: '0.9rem' }}>{value}</span>
                                                            {pct !== null && <span className="percentage-cell" style={{ fontWeight: 400 }}> ({pct}%)</span>}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            )}
            <TeamGamesVideoPlayer
                t={t}
                tagName={videoTitle}
                open={videoOpen}
                onClose={(flag) => {
                    setVideoOpen(false);

                    if (flag) setRefresh((r) => !r);
                }}
                video_url={videoURL}
                tagList={playData}
            />
            <GameExportToEdits t={t} open={exportOpen} onClose={() => setExportOpen(false)} tagList={playData} isTeams={false} />
        </Box>
    );
};

export default TeamGames;
